// apps/office/src/pages/office/ProjectCreate.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Trash2, ShoppingCart, CreditCard,
  Tag, Percent, Truck, Building2, Shield, AlertCircle,
  CheckCircle, Edit, X, Package, DollarSign, Users,
  MapPin, // ✅ MapPin is imported here
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import pb from '../../lib/pocketbaseClient';
import { useAuth } from '../../contexts/AuthContext';

const formatPrice = (price) => {
  if (!price) return '₹0';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(price);
};

const GST_PERCENT = 0.18;
const PLATFORM_FEE_PERCENT = 0.01;
const SHIPPING_RATES = { west_bengal: { normal: 499, speed: 999 }, other_states: { normal: 999, speed: 1499 } };

// ─── Main Component ──────────────────────────────────────────────
const ProjectCreate = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();

  const [selectedItems, setSelectedItems] = useState([]);
  const [showPriceList, setShowPriceList] = useState(false);
  const [searchProducts, setSearchProducts] = useState('');

  const [projectName, setProjectName] = useState('');
  const [description, setDescription] = useState('');
  const [buildingType, setBuildingType] = useState('');
  const [packageType, setPackageType] = useState('');
  const [discount, setDiscount] = useState(0);
  const [isHardCopy, setIsHardCopy] = useState(false);
  const [shippingMethod, setShippingMethod] = useState('normal');
  const [isGST, setIsGST] = useState(false);
  const [gstNumber, setGstNumber] = useState('');
  const [gstName, setGstName] = useState('');
  const [gstAddress, setGstAddress] = useState('');
  const [locationName, setLocationName] = useState('');
  const [userLocation, setUserLocation] = useState(null);
  const [pincode, setPincode] = useState('');
  const [locationDetected, setLocationDetected] = useState(false);
  const [paymentTermCount, setPaymentTermCount] = useState(1);
  const [paymentTerms, setPaymentTerms] = useState([{ term: 1, amount: 0, tasks: [] }]);
  const [isLoading, setIsLoading] = useState(false);
  const [pincodeError, setPincodeError] = useState('');

  // ─── Fetch Price List Items ──────────────────────────────────
  const { data: priceItems = [], isLoading: priceLoading, error: priceError } = useQuery({
    queryKey: ['priceList'],
    queryFn: () => pb.collection('price_list_items').getFullList({ sort: 'itemId' }),
    retry: 1,
  });

  // ─── Fetch Lead Data ──────────────────────────────────────────
  const { data: lead, isLoading: leadLoading, error: leadError } = useQuery({
    queryKey: ['lead', leadId],
    queryFn: () => pb.collection('leads').getOne(leadId, { expand: 'assignedSales' }),
    enabled: !!leadId,
    retry: 1,
  });

  // ─── Show errors ──────────────────────────────────────────────
  useEffect(() => {
    if (priceError) {
      toast.error('Failed to load price list: ' + priceError.message);
    }
    if (leadError) {
      toast.error('Failed to load client: ' + leadError.message);
    }
  }, [priceError, leadError]);

  // ─── Filter Price Items ──────────────────────────────────────
  const filteredItems = useMemo(() => {
    return priceItems.filter((item) => {
      const search = searchProducts.toLowerCase();
      return (
        item.itemId?.toLowerCase().includes(search) ||
        item.name?.toLowerCase().includes(search) ||
        item.packageType?.toLowerCase().includes(search) ||
        item.buildingType?.toLowerCase().includes(search)
      );
    });
  }, [priceItems, searchProducts]);

  // ─── Calculations ─────────────────────────────────────────────
  const subtotal = selectedItems.reduce((sum, item) => sum + (item.sellingPrice || item.mrp || 0) * (item.quantity || 1), 0);
  const gstAmount = isGST && gstNumber ? subtotal * GST_PERCENT : 0;
  const shippingCost = (() => {
    if (!isHardCopy || !locationDetected) return 0;
    const rates = userLocation === 'west_bengal' ? SHIPPING_RATES.west_bengal : SHIPPING_RATES.other_states;
    return shippingMethod === 'speed' ? rates.speed : rates.normal;
  })();
  const platformFee = (subtotal + gstAmount + shippingCost) * PLATFORM_FEE_PERCENT;
  const totalBeforeDiscount = subtotal + gstAmount + shippingCost + platformFee;
  const discountAmount = (totalBeforeDiscount * discount) / 100;
  const finalTotal = totalBeforeDiscount - discountAmount;

  // ─── Update payment terms when total changes ─────────────────
  useEffect(() => {
    if (paymentTermCount > 1 && finalTotal > 0) {
      const baseAmount = finalTotal / paymentTermCount;
      setPaymentTerms(
        Array.from({ length: paymentTermCount }, (_, i) => ({
          term: i + 1,
          amount: Math.round(baseAmount),
          tasks: [],
        }))
      );
    }
  }, [paymentTermCount, finalTotal]);

  // ─── Handlers ──────────────────────────────────────────────────
  const handleAddItem = (item) => {
    const existing = selectedItems.find((i) => i.id === item.id);
    if (existing) {
      setSelectedItems((prev) =>
        prev.map((i) =>
          i.id === item.id ? { ...i, quantity: (i.quantity || 1) + 1 } : i
        )
      );
    } else {
      setSelectedItems((prev) => [...prev, { ...item, quantity: 1 }]);
    }
    setShowPriceList(false);
    toast.success(`✅ ${item.name || item.packageType} added`);
  };

  const handleRemoveItem = (id) => {
    setSelectedItems((prev) => prev.filter((i) => i.id !== id));
  };

  const handleQuantityChange = (id, qty) => {
    if (qty < 1) return;
    setSelectedItems((prev) =>
      prev.map((i) => (i.id === id ? { ...i, quantity: qty } : i))
    );
  };

  const handleTermAmountChange = (index, value) => {
    const updated = [...paymentTerms];
    updated[index].amount = parseFloat(value) || 0;
    setPaymentTerms(updated);
  };

  // ─── Pincode Detection ────────────────────────────────────────
  const handlePincodeSubmit = async () => {
    if (pincode.length !== 6) {
      setPincodeError('Enter a valid 6-digit pincode');
      return;
    }
    setIsLoading(true);
    setPincodeError('');
    try {
      const res = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
      const data = await res.json();
      if (data && data[0] && data[0].Status === 'Success') {
        const state = data[0].PostOffice[0].State;
        setLocationName(state);
        const isWB = state.toLowerCase().includes('west bengal');
        setUserLocation(isWB ? 'west_bengal' : 'other_states');
        setLocationDetected(true);
        toast.success(`📍 Location: ${state}`);
      } else {
        setPincodeError('Invalid pincode');
      }
    } catch {
      setPincodeError('Network error');
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Create Project ────────────────────────────────────────────
  const createProjectMutation = useMutation({
    mutationFn: async (data) => {
      if (!leadId) throw new Error('Lead ID is missing');
      const projectData = {
        leadId: leadId,
        name: data.name || `${data.packageType} - ${data.buildingType}`,
        description: data.description || '',
        buildingType: data.buildingType,
        packageType: data.packageType,
        totalAmount: data.subtotal,
        discount: data.discount,
        finalAmount: data.finalTotal,
        gstAmount: data.gstAmount,
        shippingCost: data.shippingCost,
        platformFee: data.platformFee,
        isHardCopy: data.isHardCopy,
        isGST: data.isGST,
        gstNumber: data.gstNumber || '',
        gstName: data.gstName || '',
        gstAddress: data.gstAddress || '',
        paymentTerms: data.paymentTerms,
        selectedItems: data.selectedItems,
        status: 'Planning',
        progress: 0,
        isPublished: false,
        createdBy: currentUser?.id,
      };
      console.log('📦 Creating project with data:', projectData);
      return pb.collection('projects').create(projectData);
    },
    onSuccess: (project) => {
      toast.success('✅ Project created successfully!');
      // Create tasks from selected items
      createTasksFromProject(project);
      queryClient.invalidateQueries(['leadProjects', leadId]);
      navigate(`/office/clients/${leadId}`);
    },
    onError: (err) => {
      console.error('❌ Create project error:', err);
      let errorMsg = err.message || 'Unknown error';
      if (err.data?.message) errorMsg = err.data.message;
      if (err.data?.data) {
        const fields = Object.entries(err.data.data)
          .map(([field, info]) => `${field}: ${info.message}`)
          .join('; ');
        errorMsg += ' (Fields: ' + fields + ')';
      }
      toast.error('Failed to create project: ' + errorMsg);
    },
  });

  // ─── Create Tasks from Selected Items ────────────────────────
  const createTasksFromProject = async (project) => {
    try {
      const tasks = selectedItems.map((item, index) => ({
        projectId: project.id,
        title: item.name || `${item.packageType} - ${item.buildingType}`,
        description: item.details || `Task for ${item.packageType}`,
        category: item.mainCategory || 'Design',
        status: 'pending',
        assignedTo: null,
        floorNumber: '',
        paymentTermIndex: (index % paymentTermCount) + 1,
        createdBy: currentUser?.id,
        // ✅ Store the price for earning calculation
        price: item.sellingPrice || item.mrp || 0,
      }));

      for (const task of tasks) {
        await pb.collection('project_tasks').create(task);
      }
      console.log('✅ Tasks created:', tasks.length);
    } catch (error) {
      console.error('Task creation error:', error);
      toast.error('Project created but tasks failed: ' + error.message);
    }
  };

  // ─── Validate Form ────────────────────────────────────────────
  const validateForm = () => {
    if (selectedItems.length === 0) {
      toast.error('Please select at least one item');
      return false;
    }
    if (!buildingType) {
      toast.error('Please select building type');
      return false;
    }
    if (!packageType) {
      toast.error('Please select package type');
      return false;
    }
    if (isHardCopy && !locationDetected) {
      toast.error('Please enter a valid pincode for shipping');
      return false;
    }
    if (isGST && !gstNumber) {
      toast.error('Please enter GST number');
      return false;
    }
    if (paymentTermCount > 1) {
      const totalTerms = paymentTerms.reduce((sum, t) => sum + t.amount, 0);
      if (Math.abs(totalTerms - finalTotal) > 1) {
        toast.error('Payment term amounts do not match total');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    createProjectMutation.mutate({
      name: projectName,
      description,
      buildingType,
      packageType,
      subtotal,
      discount,
      finalTotal,
      gstAmount,
      shippingCost,
      platformFee,
      isHardCopy,
      isGST,
      gstNumber,
      gstName,
      gstAddress,
      paymentTerms: paymentTermCount > 1 ? paymentTerms : [{ term: 1, amount: finalTotal, tasks: [] }],
      selectedItems: selectedItems.map((i) => ({
        id: i.id,
        name: i.name,
        itemId: i.itemId,
        sellingPrice: i.sellingPrice || i.mrp,
        quantity: i.quantity,
        packageType: i.packageType,
        buildingType: i.buildingType,
      })),
    });
  };

  // ─── Loading / Error States ──────────────────────────────────
  if (leadLoading) return <div className="flex justify-center items-center h-64">Loading client...</div>;
  if (leadError) {
    return (
      <div className="text-center py-16">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-600 font-semibold">Failed to load client</p>
        <p className="text-sm text-slate-500">{leadError.message}</p>
        <Button onClick={() => navigate('/office/clients')} className="mt-4">Back to Clients</Button>
      </div>
    );
  }
  if (!lead) return <div className="text-center py-16">Client not found.</div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 p-6 shadow-xl">
        <div className="relative flex flex-wrap justify-between items-center gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/office/clients/${leadId}`)}
              className="text-white/80 hover:text-white hover:bg-white/20 gap-2"
            >
              <ArrowLeft className="w-5 h-5" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white">Create Project</h1>
              <p className="text-white/80 text-sm">For: {lead.name}</p>
            </div>
          </div>
          <Button
            onClick={handleSubmit}
            disabled={createProjectMutation.isPending}
            className="bg-white text-indigo-600 hover:bg-indigo-50 shadow-lg"
          >
            {createProjectMutation.isPending ? 'Creating...' : 'Create Project'}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* ─── Left: Selected Items & Form ─────────────────────── */}
        <div className="lg:col-span-2 space-y-6">
          {/* Selected Items */}
          <Card>
            <CardHeader className="flex flex-row justify-between items-center">
              <CardTitle className="flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-amber-500" />
                Selected Items ({selectedItems.length})
              </CardTitle>
              <Button size="sm" onClick={() => setShowPriceList(true)} variant="outline">
                <Plus className="w-4 h-4 mr-1" /> Add Items
              </Button>
            </CardHeader>
            <CardContent>
              {selectedItems.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <p>No items selected. Click "Add Items" to choose from price list.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {selectedItems.map((item) => (
                    <div
                      key={item.id}
                      className="flex flex-wrap items-center justify-between gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-800 truncate">
                          {item.name || item.packageType}
                        </p>
                        <p className="text-xs text-slate-500">
                          {item.itemId} • {item.buildingType}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center border border-slate-200 rounded">
                          <button
                            onClick={() => handleQuantityChange(item.id, (item.quantity || 1) - 1)}
                            className="px-2 py-1 hover:bg-slate-200 transition"
                          >
                            -
                          </button>
                          <span className="px-3 py-1 text-sm font-medium">{item.quantity || 1}</span>
                          <button
                            onClick={() => handleQuantityChange(item.id, (item.quantity || 1) + 1)}
                            className="px-2 py-1 hover:bg-slate-200 transition"
                          >
                            +
                          </button>
                        </div>
                        <span className="font-bold text-amber-600 text-sm">
                          {formatPrice((item.sellingPrice || item.mrp) * (item.quantity || 1))}
                        </span>
                        <button
                          onClick={() => handleRemoveItem(item.id)}
                          className="text-red-400 hover:text-red-600 transition"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Project Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Tag className="w-5 h-5 text-indigo-500" /> Project Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Project Name</Label>
                <Input
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder={`${lead.name} - Project`}
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Project description..."
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Building Type *</Label>
                  <Select value={buildingType} onValueChange={setBuildingType}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {['Single Storied', 'Two Storied (G+1)', 'Three Storied (G+2)', 'Four Storied (G+3)'].map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Package Type *</Label>
                  <Select value={packageType} onValueChange={setPackageType}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {['Standard Package', 'Advance Package', 'Premium Package'].map((p) => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Discount */}
              <div>
                <Label className="flex items-center gap-1">
                  <Percent className="w-4 h-4 text-green-500" /> Discount (%)
                </Label>
                <Input
                  type="number"
                  value={discount}
                  onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
                  min="0"
                  max="100"
                  className="border-green-200"
                />
              </div>

              {/* Hard Copy */}
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isHardCopy}
                    onChange={(e) => setIsHardCopy(e.target.checked)}
                    className="w-4 h-4 text-amber-600 rounded"
                  />
                  <span className="text-sm font-medium text-slate-700 flex items-center gap-1">
                    <Truck className="w-4 h-4 text-amber-500" /> Hard Copy Required?
                  </span>
                </label>
                {isHardCopy && (
                  <div className="mt-3 space-y-3">
                    <div>
                      <label className="text-xs font-medium text-slate-600 flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> Delivery Pincode
                      </label>
                      <div className="flex gap-2 mt-1">
                        <input
                          type="text"
                          placeholder="6-digit pincode"
                          value={pincode}
                          onChange={(e) => {
                            setPincode(e.target.value.replace(/\D/g, '').slice(0, 6));
                            setPincodeError('');
                          }}
                          className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500/20 outline-none"
                          maxLength="6"
                          disabled={locationDetected}
                        />
                        {!locationDetected ? (
                          <button
                            onClick={handlePincodeSubmit}
                            disabled={isLoading || pincode.length !== 6}
                            className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition disabled:opacity-50 text-sm font-medium"
                          >
                            {isLoading ? '...' : 'Check'}
                          </button>
                        ) : (
                          <button
                            onClick={() => {
                              setLocationDetected(false);
                              setUserLocation(null);
                              setLocationName('');
                              setPincode('');
                            }}
                            className="px-3 py-2 text-sm text-red-500 hover:text-red-700"
                          >
                            Change
                          </button>
                        )}
                      </div>
                      {pincodeError && <p className="text-xs text-red-500">{pincodeError}</p>}
                      {locationDetected && locationName && (
                        <p className="text-xs text-green-600 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" /> {locationName}
                        </p>
                      )}
                    </div>
                    {locationDetected && (
                      <div className="flex gap-2">
                        {['normal', 'speed'].map((method) => {
                          const label = method === 'normal' ? 'Normal Post' : 'Speed Post';
                          const rate = method === 'normal'
                            ? userLocation === 'west_bengal' ? 499 : 999
                            : userLocation === 'west_bengal' ? 999 : 1499;
                          return (
                            <label
                              key={method}
                              className={`flex items-center gap-2 cursor-pointer bg-white px-3 py-1.5 rounded-lg border flex-1 ${
                                shippingMethod === method ? 'border-amber-500 ring-1 ring-amber-500' : 'border-slate-200'
                              }`}
                            >
                              <input
                                type="radio"
                                name="shipping"
                                value={method}
                                checked={shippingMethod === method}
                                onChange={() => setShippingMethod(method)}
                                className="text-amber-600"
                              />
                              <div>
                                <span className="text-sm font-medium">{label}</span>
                                <span className="text-xs text-slate-500 block">{formatPrice(rate)}</span>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* GST */}
              <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-xl">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isGST}
                    onChange={(e) => setIsGST(e.target.checked)}
                    className="w-4 h-4 text-indigo-600 rounded"
                  />
                  <span className="text-sm font-medium text-slate-700 flex items-center gap-1">
                    <Building2 className="w-4 h-4 text-indigo-500" /> GST Bill Required
                  </span>
                </label>
                {isGST && (
                  <div className="mt-3 space-y-2">
                    <Input
                      type="text"
                      placeholder="GST Number"
                      value={gstNumber}
                      onChange={(e) => setGstNumber(e.target.value)}
                    />
                    <Input
                      type="text"
                      placeholder="Company / Name"
                      value={gstName}
                      onChange={(e) => setGstName(e.target.value)}
                    />
                    <Input
                      type="text"
                      placeholder="Billing Address"
                      value={gstAddress}
                      onChange={(e) => setGstAddress(e.target.value)}
                    />
                    <p className="text-xs text-indigo-600">18% GST added on product amount</p>
                  </div>
                )}
              </div>

              {/* Payment Terms */}
              <div className="border-t border-slate-200 pt-4">
                <Label className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-amber-500" /> Payment Terms
                </Label>
                <div className="flex gap-3 mt-2">
                  {[1, 2, 3].map((num) => (
                    <label
                      key={num}
                      className={`flex items-center gap-2 cursor-pointer px-4 py-2 rounded-lg border ${
                        paymentTermCount === num
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="terms"
                        value={num}
                        checked={paymentTermCount === num}
                        onChange={() => setPaymentTermCount(num)}
                        className="text-amber-600"
                      />
                      <span className="font-medium">{num} {num === 1 ? 'Payment' : 'Payments'}</span>
                    </label>
                  ))}
                </div>

                {paymentTermCount > 1 && (
                  <div className="mt-3 space-y-2">
                    <p className="text-sm text-slate-500">Distribute total among {paymentTermCount} payments:</p>
                    {paymentTerms.map((term, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <span className="text-sm font-medium w-12">Term {term.term}</span>
                        <Input
                          type="number"
                          value={term.amount}
                          onChange={(e) => handleTermAmountChange(idx, e.target.value)}
                          className="flex-1"
                          placeholder="Amount"
                        />
                        <span className="text-xs text-slate-400">
                          {((term.amount / finalTotal) * 100 || 0).toFixed(0)}%
                        </span>
                      </div>
                    ))}
                    <p className="text-xs text-slate-400">
                      Total: {formatPrice(paymentTerms.reduce((sum, t) => sum + t.amount, 0))} of {formatPrice(finalTotal)}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ─── Right: Summary ────────────────────────────────────── */}
        <div className="lg:col-span-1">
          <Card className="sticky top-24">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-amber-500" /> Project Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Items ({selectedItems.length})</span>
                <span className="font-medium">{formatPrice(subtotal)}</span>
              </div>
              {isGST && gstNumber && (
                <div className="flex justify-between">
                  <span className="text-slate-500">GST (18%)</span>
                  <span>{formatPrice(gstAmount)}</span>
                </div>
              )}
              {isHardCopy && locationDetected && (
                <div className="flex justify-between">
                  <span className="text-slate-500">Shipping</span>
                  <span>{formatPrice(shippingCost)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-slate-500">Platform Fee (1%)</span>
                <span>{formatPrice(platformFee)}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Discount ({discount}%)</span>
                  <span>-{formatPrice(discountAmount)}</span>
                </div>
              )}
              <div className="border-t border-slate-200 pt-2">
                <div className="flex justify-between font-bold text-lg">
                  <span>Total</span>
                  <span className="text-amber-600">{formatPrice(finalTotal)}</span>
                </div>
              </div>
              {paymentTermCount > 1 && (
                <div className="border-t border-slate-200 pt-2 mt-2">
                  <p className="text-xs text-slate-500 font-medium">Payment Breakdown:</p>
                  {paymentTerms.map((term) => (
                    <div key={term.term} className="flex justify-between text-xs">
                      <span>Term {term.term}</span>
                      <span className="font-medium">{formatPrice(term.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
              <Button
                onClick={handleSubmit}
                disabled={createProjectMutation.isPending}
                className="w-full mt-4 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
              >
                {createProjectMutation.isPending ? 'Creating...' : '🚀 Create Project'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ─── Price List Dialog ──────────────────────────────────── */}
      <Dialog open={showPriceList} onOpenChange={setShowPriceList}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-orange-600">
              <Tag className="w-6 h-6 inline mr-2" /> Select Items
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              placeholder="Search items..."
              value={searchProducts}
              onChange={(e) => setSearchProducts(e.target.value)}
              className="border-orange-200"
            />
            {priceLoading ? (
              <p className="text-center py-8 text-slate-400">Loading...</p>
            ) : filteredItems.length === 0 ? (
              <p className="text-center py-8 text-slate-400">No items found</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {filteredItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleAddItem(item)}
                    className="text-left p-3 border border-slate-200 rounded-lg hover:border-orange-400 hover:bg-orange-50 transition group"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-slate-800 text-sm">
                          {item.name || item.packageType}
                        </p>
                        <p className="text-xs text-slate-500">{item.itemId}</p>
                        <p className="text-xs text-slate-500">{item.buildingType}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-amber-600 text-sm">{formatPrice(item.sellingPrice || item.mrp)}</p>
                        {item.discountPercent > 0 && (
                          <p className="text-xs text-slate-400 line-through">{formatPrice(item.mrp)}</p>
                        )}
                        <Plus className="w-4 h-4 text-orange-400 opacity-0 group-hover:opacity-100 transition" />
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPriceList(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectCreate;